import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Trash2 } from "lucide-react";
import type { NotificationSettings } from "@/hooks/useNotificationSettings";
import { supabase } from "@/integrations/supabase/client";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface TelegramConfigProps {
  settings: NotificationSettings;
  onSave: (updates: Partial<NotificationSettings>) => Promise<void>;
}

export function TelegramConfig({ settings, onSave }: TelegramConfigProps) {
  const [botToken, setBotToken] = useState(settings.telegram_bot_token || "");
  const [chatId, setChatId] = useState(settings.telegram_chat_id || "");
  const [enabled, setEnabled] = useState(settings.telegram_enabled);
  const [testing, setTesting] = useState(false);
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    setBotToken(settings.telegram_bot_token || "");
    setChatId(settings.telegram_chat_id || "");
    setEnabled(settings.telegram_enabled);
  }, [settings]);

  const handleSave = async () => {
    // Safety check: At least one must be enabled
    if (!enabled && !settings.email_enabled && !settings.discord_enabled) {
      toast.error("Security Override: At least one notification channel must be active.");
      setEnabled(true);
      return;
    }

    try {
      await onSave({
        telegram_bot_token: botToken || null,
        telegram_chat_id: chatId || null,
        telegram_enabled: enabled,
      });
      toast.success("Telegram settings saved");
    } catch (err: any) {
      toast.error(err.message || "Failed to save Telegram settings");
    }
  };

  const handleDisconnect = async () => {
    try {
      await onSave({ telegram_bot_token: null, telegram_chat_id: null, telegram_enabled: false });
      setBotToken("");
      setChatId("");
      setVerified(false);
      toast.success("Telegram credentials purged from grid.");
    } catch (err: any) {
      toast.error("Failed to disconnect.");
    }
  };

  const handleTest = async () => {
    if (!botToken || !chatId) return toast.error("Enter bot token and chat ID");
    setTesting(true);
    setVerified(false);
    try {
      const { data, error } = await supabase.functions.invoke("test-integration", {
        body: { type: "telegram", botToken, chatId },
      });
      if (error || (data && data.error)) {
        toast.error(error?.message || data?.error || "Failed to send test message");
      } else {
        setVerified(true);
        toast.success("Test message sent");
      }
    } catch {
      toast.error("Failed to reach Telegram API");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <p className="text-sm font-black uppercase tracking-tight">Telegram Infrastructure</p>
          <p className="text-[10px] text-muted-foreground uppercase font-bold tracking-widest">Global Alert Network</p>
        </div>
        <Switch checked={enabled} onCheckedChange={setEnabled} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Quick Option */}
        <div className="border-2 border-primary/20 bg-primary/5 p-4 rounded-none space-y-3 group hover:border-primary transition-colors flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
              <span className="text-[10px] font-black uppercase">Stealth Link (Automatic)</span>
            </div>
            <p className="text-xs font-medium leading-relaxed">
              Launch our tactical bot. It will automatically detect your deployment and sync with your secure terminal.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 mt-4">
            <Button
              variant="outline"
              onClick={async () => {
                const { data } = await supabase.auth.getSession();
                if (data.session?.user) {
                  const botUsername = import.meta.env.VITE_TELEGRAM_BOT_USERNAME || "Uptimewingbot";
                  window.open(`https://t.me/${botUsername}?start=${data.session.user.id}`, "_blank");
                }
              }}
              className="flex-1 h-10 border-2 border-foreground bg-primary text-primary-foreground hover:bg-foreground hover:text-background font-black uppercase text-[10px] shadow-brutal-sm"
            >
              Connect Bot
            </Button>
          </div>
        </div>

        {/* Manual Option with Setup Wizard */}
        <div className="border-2 border-border p-4 rounded-none space-y-4">
          <div className="space-y-1">
            <span className="text-[10px] font-black uppercase opacity-40">Manual Telegram Setup Wizard</span>
            <div className="bg-muted/30 border border-foreground/10 p-3 text-[10px] font-bold uppercase tracking-tight text-muted-foreground space-y-1.5 mt-2">
              <p className="text-foreground font-black">Telegram Integration Wizard:</p>
              <p className="flex gap-2"><span>1.</span> Chat with `@BotFather` on Telegram to create a Bot.</p>
              <p className="flex gap-2"><span>2.</span> Copy the HTTP API token they give you and paste it in "Bot Token".</p>
              <p className="flex gap-2"><span>3.</span> Add your bot to your target Group/Channel (or start a private chat).</p>
              <p className="flex gap-2"><span>4.</span> Send a message and search `@JsonDumpBot` or use `@userinfobot` to retrieve your Chat ID, then paste it in "Target Chat ID".</p>
            </div>
          </div>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-[10px] font-black uppercase opacity-60">Bot Token</Label>
              <Input
                value={botToken}
                onChange={(e) => { setBotToken(e.target.value); setVerified(false); }}
                placeholder="123456:ABC-DEF..."
                className="h-10 rounded-none border-2 border-foreground font-mono text-[10px] focus-visible:ring-primary shadow-brutal-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-black uppercase opacity-60">Target Chat ID</Label>
              <Input
                value={chatId}
                onChange={(e) => { setChatId(e.target.value); setVerified(false); }}
                placeholder="-1001234567890"
                className="h-10 rounded-none border-2 border-foreground font-mono text-[10px] focus-visible:ring-primary shadow-brutal-sm"
              />
            </div>
            {(botToken || chatId) && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-[9px] font-black uppercase text-status-down hover:bg-status-down/10 gap-1.5 h-8 mt-2"
                  >
                    <Trash2 className="h-3 w-3" /> Disconnect StealthBot
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="glass-strong rounded-none border-4 border-foreground bg-background shadow-brutal p-6">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-base font-black uppercase tracking-tight">Disconnect Telegram</AlertDialogTitle>
                    <AlertDialogDescription className="text-xs font-bold uppercase tracking-wider text-muted-foreground mt-2">
                      Are you sure you want to disconnect Telegram? This will purge your bot link.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter className="mt-4 gap-2 flex-row justify-end">
                    <AlertDialogCancel className="rounded-none border-2 border-foreground bg-background text-[10px] font-black uppercase tracking-widest shadow-[2px_2px_0_0_#000] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-all mt-0">
                      Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDisconnect}
                      className="rounded-none border-2 border-foreground bg-status-down text-white hover:bg-foreground hover:text-background font-black uppercase text-[10px] shadow-[2px_2px_0_0_#000] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-all"
                    >
                      Disconnect
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 pt-2">
        <Button onClick={handleSave} className="h-9 rounded-none border-2 border-foreground bg-foreground text-background font-black uppercase text-[10px] shadow-brutal-sm hover:bg-primary transition-all">
          Save Configuration
        </Button>
        <Button
          variant="outline"
          onClick={handleTest}
          disabled={testing || !botToken || !chatId}
          className="h-9 rounded-none border-2 border-foreground bg-background font-black uppercase text-[10px] shadow-brutal-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all"
        >
          {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
          {testing ? "Sending Pulse..." : "Test Pulse"}
        </Button>
        {verified && (
          <div className="flex items-center gap-1.5 ml-auto bg-status-up/10 px-3 py-1 border-2 border-status-up shadow-brutal-sm">
            <CheckCircle2 className="h-3 w-3 text-status-up" />
            <span className="text-[10px] font-black text-status-up uppercase">Verified</span>
          </div>
        )}
      </div>
    </div >
  );
}
